import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import ReactTable from "react-table";
import { confirmAlert } from "react-confirm-alert";
import { ToastContainer, toast } from "react-toastify";
import { format } from "date-fns";
import Header from "../../../Components/Header";
import LeftMenu from "../../../Components/LeftMenu";
import AddCampaignMediumMaster from "./AddCampaignMediumMaster";
import { deleteCampaignMediumById, getAllCampaignMedium, saveUpdateCampaignMedium } from "../../../Utils/Services/CampaignMedium";

toast.configure();

const ENABLE_ADD_EDIT_DELETE = false;

const getCellValue = (original, field) => original?.[field] ?? null;

const iconButtonStyle = { cursor: "pointer", background: "none", border: "none", padding: 0 };

const campaignRowShape = PropTypes.shape({
	campaignMediumId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	campaignMedium: PropTypes.string,
	campaignName: PropTypes.string,
	campaignPlatform: PropTypes.string,
	campaignMediumDescription: PropTypes.string,
	productType: PropTypes.string,
	startDate: PropTypes.string,
	endDate: PropTypes.string,
});

const ActionCell = ({ original, onView, onEdit, onDelete, showEditDelete }) => (
	<div style={{ display: "flex", justifyContent: "center", gap: "8px" }}>
		<button
			type="button"
			className="fas fa-eye text-secondary"
			style={iconButtonStyle}
			onClick={() => onView(original)}
			title="View"
			aria-label="View campaign medium"
		/>
		{showEditDelete && (
			<>
				<button
					type="button"
					className="fa fa-trash"
					style={iconButtonStyle}
					onClick={() => onDelete(original.campaignMediumId)}
					title="Delete"
					aria-label="Delete campaign medium"
				/>
				<button
					type="button"
					className="fas fa-user-edit"
					style={iconButtonStyle}
					onClick={() => onEdit(original)}
					title="Edit"
					aria-label="Edit campaign medium"
				/>
			</>
		)}
	</div>
);

ActionCell.propTypes = {
	original: campaignRowShape.isRequired,
	onView: PropTypes.func.isRequired,
	onEdit: PropTypes.func.isRequired,
	onDelete: PropTypes.func.isRequired,
	showEditDelete: PropTypes.bool.isRequired,
};

const createColumns = ({ onView, onEdit, onDelete, showEditDelete }) => [
	{
		Header: "SR. No",
		width: 50,
		Cell: (row) => row.index + 1,
	},
	{
		Header: "Campaign Medium",
		Cell: (row) => getCellValue(row.original, "campaignMedium"),
	},
	{
		Header: "Campaign Name",
		Cell: (row) => getCellValue(row.original, "campaignName"),
	},
	{
		Header: "Campaign Platform",
		Cell: (row) => getCellValue(row.original, "campaignPlatform"),
	},
	{
		Header: "Campaign Medium Description",
		Cell: (row) => getCellValue(row.original, "campaignMediumDescription"),
	},
	{
		Header: "Product Type",
		Cell: (row) => getCellValue(row.original, "productType"),
	},
	{
		Header: "Campaign Start Date",
		Cell: (row) => getCellValue(row.original, "startDate"),
	},
	{
		Header: "Campaign End Date",
		Cell: (row) => getCellValue(row.original, "endDate"),
	},
	{
		Header: "Action",
		Cell: (row) => (
			<ActionCell
				original={row.original}
				onView={onView}
				onEdit={onEdit}
				onDelete={onDelete}
				showEditDelete={showEditDelete}
			/>
		),
	},
];

const CampaignMediumMaster = (props) => {
	let [showAddModal, setShowAddModal] = useState(false);
	let [showEditModal, setShowEditModal] = useState(false);
	let [showViewModal, setShowViewModal] = useState(false);
	let [campaignMediumData, setCampaignMediumData] = useState([]);
	let [editData, setEditData] = useState({});
	let [viewData, setViewData] = useState({});

	useEffect(() => {
		getCampaignMediumMaster();
	}, []);

    const getCampaignMediumMaster = () => {
		getAllCampaignMedium().then((response) => {
			if (response.error) {
				return;
			}
			if (response.data?.error) {
				toast.error(response.data.message, {
					type: toast.TYPE.ERROR,
					autoClose: 2000,
				});
			} else {
				setCampaignMediumData(response.data.data);
			}
		});
	};

    const deleteCampaignMedium = (id) => {
        confirmAlert({
            title: "Confirm to delete",
            message: "Are you sure to do this.",
            buttons: [
                {
                    label: "Yes",
                    onClick: () => {
                        deleteCampaignMediumById(id)
                            .then((res) => {
                                if (res.error) {
                                    toast.error(res.error);
                                } else {
                                    toast.success("Deleted Successfully");
                                    setCampaignMediumData();
                                    getCampaignMediumMaster();
                                }
                            })
                            .catch((err) => {
                                console.log("err", err);
                            });
                    },
                },
                {
					label: "No",
					onClick: () => {
						console.log("no");
					},
				},
			],
		});
	};

	const handleAddCampaignModal = () => {
		setShowAddModal(!showAddModal);
	};

	const handleEditCampaignModal = (obj) => {
		setShowEditModal(!showEditModal);
		setEditData(obj);
	};

	const handleViewCampaignModal = (obj) => {
		setShowViewModal(!showViewModal);
		setViewData(obj);
	};

    const addCampaignMedium = (obj) => {
		setShowAddModal(!showAddModal);
		let newCampaign = {
			...obj,
			startDate: format(obj.startDate, "yyyy-MM-dd"),
			endDate: format(obj.endDate, "yyyy-MM-dd"),
		};
		saveUpdateCampaignMedium(newCampaign)
			.then((res) => {
				if (res.error) {
					toast.error(res.data.message);
				} else if (res.data?.error) {
					toast.error(res.data.message);
				} else {
					toast.success(res.data.message);
					getCampaignMediumMaster();
				}
			})
            .catch((err) => {
				console.log("err", err);
			});
	};

    const editCampaignMedium = (obj) => {
		setShowEditModal(!showEditModal);
		let editedCampaign = {
			...obj,
			startDate: format(obj.startDate, "yyyy-MM-dd"),
			endDate: format(obj.endDate, "yyyy-MM-dd"),
		};
		saveUpdateCampaignMedium(editedCampaign)
			.then((res) => {
				if (res.error) {
					toast.error("Server Error");
				} else if (res.data?.error) {
					toast.error("Error Updating Campaign");
				} else {
					toast.success("Campaign Updated Successfully");
					getCampaignMediumMaster();
				}
			})
			.catch((err) => {
				console.log("err", err);
			});
	};

	const columns = createColumns({
		onView: handleViewCampaignModal,
		onEdit: handleEditCampaignModal,
		onDelete: deleteCampaignMedium,
		showEditDelete: ENABLE_ADD_EDIT_DELETE,
	});

    return (
		<div className="dash_grid">
			<ToastContainer />
			<LeftMenu />
			<main className="bg-white">
				<Header {...props} />
				<section className="container-fluid mb-5">
					<h5 className="text-center mt-5 mx-auto user-box"> Campaign Medium Master</h5>
					{ENABLE_ADD_EDIT_DELETE && (
						<div className="d-flex align-items-center mt-5">
							<button disabled={false} onClick={handleAddCampaignModal} className="add-btn ml-auto btn btn-info btn-lg">
								Add
							</button>
							{showAddModal && (
								<AddCampaignMediumMaster action={"add"} handleAddCampaignModal={handleAddCampaignModal} addCampaignMedium={addCampaignMedium} />
							)}
							{showEditModal && (
								<AddCampaignMediumMaster
									action={"edit"}
									editData={editData}
									handleEditCampaignModal={handleEditCampaignModal}
									editCampaignMedium={editCampaignMedium}
								/>
							)}
						</div>
					)}
					{showViewModal && (
						<AddCampaignMediumMaster
							action={"view"}
							editData={viewData}
							handleViewCampaignModal={handleViewCampaignModal}
						/>
					)}
					<div className={`mt-4 react-table`}>
						<ReactTable data={campaignMediumData} columns={columns} defaultPageSize={10} />
					</div>
				</section>
			</main>
		</div>
	);
};
export default CampaignMediumMaster;
